import React, { useEffect, useMemo, useState, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { WikiPage } from '../../types/wiki';
import {
  ChevronDown,
  CircleCheck,
  Download,
  Ellipsis,
  History,
  List,
  Lock,
  Pencil,
  Printer,
  Trash2,
} from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { Button, EntityIcon, IconButton, Menu, MenuItem, Pill } from '../ui';
import { formatDateTime, personName, timeAgo, WIKI_STATUS } from './wikiFormat';

interface WikiPageDisplayProps {
  wikiPage: WikiPage;
  onEdit?: () => void;
  onViewHistory?: () => void;
  /** Adds Delete to the ⋯ menu */
  onDelete?: () => void;
  /** Disables Delete in the menu, e.g. for locked pages */
  deleteDisabled?: boolean;
  showActions?: boolean;
  /** Compact variant hides the page-chrome header (used when embedded in detail pages) */
  compact?: boolean;
  className?: string;
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Table of Contents builder                                                */
/* ────────────────────────────────────────────────────────────────────────── */

interface TocItem {
  id: string;
  text: string;
  level: number;
}

/**
 * Parse heading tags from sanitised HTML and return a flat list suitable
 * for building a table of contents.
 */
function buildTableOfContents(html: string): TocItem[] {
  if (!html) return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const headings = doc.querySelectorAll('h1, h2, h3');
  const items: TocItem[] = [];

  headings.forEach((el, idx) => {
    const text = el.textContent?.trim() || '';
    if (!text) return;
    const level = parseInt(el.tagName.charAt(1), 10);
    const id = `section-${idx}`;
    items.push({ id, text, level });
  });

  return items;
}

/**
 * Inject `id` attributes into heading tags so the TOC links work.
 */
function injectHeadingIds(html: string): string {
  if (!html) return '';
  let idx = 0;
  return html.replace(/<(h[1-3])(\s[^>]*)?>/gi, (match, tag, attrs) => {
    const id = `section-${idx++}`;
    // If there's already an id attribute, replace it
    if (attrs && /id\s*=/.test(attrs)) {
      return `<${tag}${attrs.replace(/id\s*=\s*["'][^"']*["']/, `id="${id}"`)}>`;
    }
    return `<${tag} id="${id}"${attrs || ''}>`;
  });
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Main component                                                           */
/* ────────────────────────────────────────────────────────────────────────── */

export const WikiPageDisplay: React.FC<WikiPageDisplayProps> = ({
  wikiPage,
  onEdit,
  onViewHistory,
  onDelete,
  deleteDisabled = false,
  showActions = true,
  compact = false,
  className = '',
}) => {
  const [tocOpen, setTocOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);

  // Sanitise HTML content
  const sanitizedHtml = useMemo(() => {
    if (!wikiPage.content) return '';
    const clean = DOMPurify.sanitize(wikiPage.content);
    return injectHeadingIds(clean);
  }, [wikiPage.content]);

  // Build table of contents
  const tocItems = useMemo(() => buildTableOfContents(sanitizedHtml), [sanitizedHtml]);
  const showToc = tocItems.length >= 3; // Only show TOC if 3+ headings
  const topLevel = useMemo(() => Math.min(...tocItems.map((item) => item.level), 3), [tocItems]);

  // Highlight the section being read
  useEffect(() => {
    if (!showToc || typeof IntersectionObserver === 'undefined') return;
    const headings = tocItems
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => !!el);
    if (!headings.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (visible.length) setActiveSection(visible[0].target.id);
      },
      { rootMargin: '-72px 0px -70% 0px' },
    );
    headings.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [showToc, tocItems]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const handleDownloadPdf = useCallback(async () => {
    // Render the page container (header + article) to PDF
    const element = document.getElementById('wiki-page-container');
    if (!element) return;

    const opt = {
      margin: [15, 15, 20, 15] as any, // Top, Left, Bottom, Right
      filename: `${wikiPage.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_report.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    await html2pdf().set(opt as any).from(element).save();
  }, [wikiPage.title]);

  const scrollToSection = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  }, []);

  const status = WIKI_STATUS[wikiPage.status] || WIKI_STATUS.draft;
  const author = personName(wikiPage.creator, wikiPage.createdBy);
  const approver = personName(wikiPage.approver, wikiPage.approvedBy);
  const hasContent = !!wikiPage.content;

  const menuItems: MenuItem[] = [
    ...(onViewHistory ? [{ label: 'History', icon: History, onSelect: onViewHistory }] : []),
    ...(hasContent
      ? [
          { label: 'Download PDF', icon: Download, onSelect: handleDownloadPdf },
          { label: 'Print', icon: Printer, onSelect: handlePrint },
        ]
      : []),
    ...(onDelete
      ? [{ label: deleteDisabled ? 'Locked' : 'Delete', icon: Trash2, danger: true, disabled: deleteDisabled, onSelect: onDelete }]
      : []),
  ];

  const tocList = (onPick?: () => void) => (
    <ul className="space-y-px">
      {tocItems.map((item) => {
        const active = activeSection === item.id;
        return (
          <li key={item.id}>
            <button
              type="button"
              onClick={() => {
                scrollToSection(item.id);
                onPick?.();
              }}
              title={item.text}
              style={{ paddingLeft: `${12 + (item.level - topLevel) * 12}px` }}
              className={`-ml-px block w-full truncate border-l py-1 pr-2 text-left text-[13px] leading-5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
                active ? 'border-primary-600 text-ink' : 'border-transparent text-neutral-500 hover:text-ink'
              }`}
            >
              {item.text}
            </button>
          </li>
        );
      })}
    </ul>
  );

  const sideToc = showToc && !compact;

  return (
    <article
      id="wiki-page-container"
      className={`font-ui text-ink ${sideToc ? 'xl:grid xl:grid-cols-[minmax(0,720px)_208px] xl:justify-between xl:gap-12' : ''} ${className}`}
    >
      <div className="min-w-0 max-w-[720px]">
        {/* ── Header ──────────────────────────────────────────────────────── */}
        {!compact && (
          <header className="mb-6 flex flex-wrap items-start justify-between gap-x-4 gap-y-3 border-b border-neutral-200/70 pb-5">
            <div className="min-w-0 flex-1">
              <h1 className="text-[24px] font-normal leading-tight tracking-[-0.03em] text-ink">
                {wikiPage.title}
                {wikiPage.isLocked && (
                  <Lock className="ml-2 inline-block h-4 w-4 align-[-1px] text-neutral-400" strokeWidth={1.75} aria-label="Locked" />
                )}
              </h1>
              <p className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] text-neutral-500">
                {wikiPage.entityName && (
                  <>
                    <span className="inline-flex min-w-0 basis-full items-center gap-1.5 sm:max-w-[260px] sm:basis-auto">
                      {(wikiPage.entityType === 'stakeholder' || wikiPage.entityType === 'organization') && (
                        <EntityIcon kind={wikiPage.entityType} size="xs" />
                      )}
                      <span className="truncate">{wikiPage.entityName}</span>
                    </span>
                    <span className="hidden text-neutral-300 sm:inline" aria-hidden="true">·</span>
                  </>
                )}
                <span title={formatDateTime(wikiPage.updatedAt)}>Updated {timeAgo(wikiPage.updatedAt)}</span>
                <span className="text-neutral-300" aria-hidden="true">·</span>
                <span className="tabular-nums">v{wikiPage.version}</span>
                {wikiPage.status !== 'approved' && (
                  <Pill tone={status.tone} className="ml-1">
                    {status.label}
                  </Pill>
                )}
              </p>
            </div>

            {showActions && (onEdit || menuItems.length > 0) && (
              <div className="no-print flex flex-shrink-0 items-center gap-1.5">
                {onEdit && (
                  <Button size="sm" variant="secondary" onClick={onEdit}>
                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
                    Edit
                  </Button>
                )}
                {menuItems.length > 0 && (
                  <Menu
                    items={menuItems}
                    trigger={(props) => (
                      <IconButton label="More actions" size="sm" {...props}>
                        <Ellipsis className="h-4 w-4" strokeWidth={1.75} />
                      </IconButton>
                    )}
                  />
                )}
              </div>
            )}
          </header>
        )}

        {/* ── Body ────────────────────────────────────────────────────────── */}
        {!hasContent ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-neutral-200 px-4 py-12 text-center">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100 text-neutral-400">
              <Pencil className="h-[18px] w-[18px]" strokeWidth={1.75} />
            </span>
            <p className="text-[13px] text-neutral-500">No content yet</p>
            {onEdit && (
              <Button size="sm" variant="secondary" onClick={onEdit}>
                Add content
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Contents disclosure where there is no room for the side list */}
            {showToc && (
              <div className={`no-print mb-6 rounded-2xl border border-neutral-200 ${sideToc ? 'xl:hidden' : ''}`}>
                <button
                  type="button"
                  onClick={() => setTocOpen(!tocOpen)}
                  aria-expanded={tocOpen}
                  className="flex h-11 w-full items-center justify-between gap-3 rounded-2xl px-4 text-[13px] text-neutral-500 transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
                >
                  <span className="inline-flex items-center gap-2">
                    <List className="h-4 w-4 text-neutral-400" strokeWidth={1.75} />
                    Contents
                    <span className="tabular-nums text-neutral-400">{tocItems.length}</span>
                  </span>
                  <ChevronDown className={`h-4 w-4 text-neutral-400 transition-transform duration-200 ${tocOpen ? 'rotate-180' : ''}`} strokeWidth={1.75} />
                </button>
                {tocOpen && (
                  <div className="animate-fade-up border-t border-neutral-200/70 py-2 pl-4 pr-2">
                    <div className="border-l border-neutral-200/70">{tocList(() => setTocOpen(false))}</div>
                  </div>
                )}
              </div>
            )}

            {/* Rendered article content */}
            <div
              className="wiki-article"
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
          </>
        )}

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        {!compact && (author || (wikiPage.status === 'approved' && wikiPage.approvedBy && wikiPage.approvedAt)) && (
          <footer className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-neutral-200/70 pt-4 text-[12px] text-neutral-400">
            {author && <span>Created by {author}</span>}
            {wikiPage.status === 'approved' && wikiPage.approvedBy && wikiPage.approvedAt && (
              <span className="inline-flex items-center gap-1.5">
                <CircleCheck className="h-3.5 w-3.5 text-emerald-600" strokeWidth={1.75} />
                Approved by {approver} on {formatDateTime(wikiPage.approvedAt)}
              </span>
            )}
          </footer>
        )}

        {/* Compact mode inline actions */}
        {compact && showActions && (onEdit || onViewHistory) && (
          <div className="no-print mt-4 flex items-center gap-2 border-t border-neutral-200/70 pt-3">
            {onEdit && (
              <Button size="xs" variant="secondary" onClick={onEdit}>
                <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
                Edit
              </Button>
            )}
            {onViewHistory && (
              <Button size="xs" variant="ghost" onClick={onViewHistory}>
                <History className="h-3.5 w-3.5" strokeWidth={1.75} />
                History
              </Button>
            )}
          </div>
        )}
      </div>

      {/* ── Side contents ─────────────────────────────────────────────────── */}
      {sideToc && hasContent && (
        <nav aria-label="Contents" className="no-print hidden xl:block">
          <div className="sticky top-[88px] max-h-[calc(100vh-112px)] overflow-y-auto pt-1 [scrollbar-width:none]">
            <p className="mb-2 pl-3 text-[12px] text-neutral-400">Contents</p>
            <div className="border-l border-neutral-200/70">{tocList()}</div>
          </div>
        </nav>
      )}
    </article>
  );
};
